import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { tenantVars } from '@/lib/colors';
import { useAuth } from '@/context/AuthContext';

const TenantContext = createContext(null);
export const useTenant = () => useContext(TenantContext);

export const ALL_CODE = '__ALL__';
export const CONSOLIDATED_PATH = '/consolidation';
// Routes that operate in "global admin" mode (all associations, SHARITI product theme)
export const GLOBAL_PATHS = ['/consolidation', '/comparatif', '/territoires', '/recruteurs', '/suivi-social'];

export function TenantProvider({ children }) {
  const { user } = useAuth();
  const [associations, setAssociations] = useState([]);
  const [current, setCurrent] = useState(null); // real association code (never ALL)
  const [loading, setLoading] = useState(true);

  const isAdmin = user?.role === 'SHARITI_ADMIN';
  const isCampaignManager = user?.role === 'CAMPAIGN_MANAGER';

  // Aggregated "all associations" mode is driven by the tenant switcher (not the
  // route), so every analytical page can operate in aggregated mode. Persisted in
  // session + seeded from the URL so it survives refreshes on global pages.
  const [allMode, setAllModeState] = useState(() => {
    if (typeof window === 'undefined') return true;
    if (GLOBAL_PATHS.includes(window.location.pathname)) return true;
    // Default to the aggregated "Toutes les associations" view on a fresh session
    // (only busted to a single association if the user explicitly picked one).
    return sessionStorage.getItem('shariti_all_mode') !== '0';
  });
  const setAllMode = (v) => {
    setAllModeState(v);
    try { sessionStorage.setItem('shariti_all_mode', v ? '1' : '0'); } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get('/associations');
        if (!active) return;
        setAssociations(data);
        const initial = user?.role === 'ASSOCIATION_USER'
          ? user.association_code
          : (data[0]?.code || null);
        setCurrent(initial);
      } catch (e) {
        // ignore
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [user]);

  const isAll = allMode && isAdmin;

  const association = useMemo(
    () => (isAll ? null : associations.find((a) => a.code === current) || null),
    [associations, current, isAll]
  );

  // Admins switch freely (incl. the aggregated mode); campaign managers may switch
  // ONLY between the associations they actually worked in (never the global mode).
  const canSwitch = isAdmin || (isCampaignManager && associations.length > 1);

  // Apply tenant CSS vars (SHARITI orange default when in consolidated mode)
  useEffect(() => {
    const vars = tenantVars(association?.primary_color);
    const root = document.documentElement;
    Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));
  }, [association]);

  return (
    <TenantContext.Provider value={{ associations, setAssociations, current, setCurrent, association, loading, isAll, allMode, setAllMode, ALL_CODE, canSwitch, isCampaignManager }}>
      {children}
    </TenantContext.Provider>
  );
}
